import { API_VERSION_DATE, createWhopFetch } from "../api/index.js";
import { apiBaseUrl } from "../auth/client.js";

const PLACEHOLDER = "https://api.whop.com";
const whopFetch = createWhopFetch();

function apiErrorMessage(payload: unknown): string | undefined {
	if (!payload || typeof payload !== "object") return undefined;
	const response = payload as Record<string, unknown>;
	if (typeof response.message === "string") return response.message;
	if (typeof response.detail === "string") return response.detail;
	if (typeof response.error === "string") return response.error;
	if (
		response.error &&
		typeof response.error === "object" &&
		typeof (response.error as Record<string, unknown>).message === "string"
	) {
		return (response.error as Record<string, string>).message;
	}
	return undefined;
}

/**
 * Thin wrapper around the API fetch proxy for hand-written commands. The proxy
 * strips the host and uses apiBaseUrl() internally, auto-fills the active
 * account, and normalizes errors to a top-level `message` — so we only need the
 * pathname (a query string in `path` is fine) and can surface `message` directly.
 */
export async function makeWhopRequest<T>(
	method: "GET" | "POST" | "PATCH" | "DELETE",
	path: string,
	body?: Record<string, unknown>,
	accessToken?: string,
): Promise<T> {
	const init: RequestInit = { method };
	if (body !== undefined) init.body = JSON.stringify(body);
	const res = accessToken
		? await fetch(`${apiBaseUrl().replace(/\/$/, "")}${path}`, {
				...init,
				headers: {
					Authorization: `Bearer ${accessToken}`,
					"Api-Version-Date": API_VERSION_DATE,
					...(body !== undefined ? { "Content-Type": "application/json" } : {}),
				},
			})
		: await whopFetch(new Request(`${PLACEHOLDER}${path}`, init));

	if (!res.ok) {
		const payload = (await res.json().catch(() => null)) as unknown;
		throw new Error(apiErrorMessage(payload) ?? res.statusText);
	}

	// DELETE (and other 204s) can return an empty body, which `res.json()` would
	// reject — treat an empty body as a successful, content-less response.
	const text = await res.text();
	return (text ? JSON.parse(text) : { success: true }) as T;
}
